// Perspective correction: map a 4-corner quad in the source onto a flat rectangle

// Solve A x = b (Gaussian elimination with partial pivoting)
function solve(A, b) {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let c = 0; c < n; c++) {
    let p = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(M[r][c]) > Math.abs(M[p][c])) p = r;
    [M[c], M[p]] = [M[p], M[c]];
    if (Math.abs(M[c][c]) < 1e-12) return null;
    for (let r = 0; r < n; r++) {
      if (r === c) continue;
      const f = M[r][c] / M[c][c];
      for (let k = c; k <= n; k++) M[r][k] -= f * M[c][k];
    }
  }
  return M.map((row, i) => row[n] / row[i]);
}

// Homography H such that H * (x, y, 1) of each `from` point lands on the matching `to` point
export function homography(from, to) {
  const A = [];
  const b = [];
  from.forEach(([x, y], i) => {
    const [u, v] = to[i];
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    b.push(v);
  });
  const h = solve(A, b);
  return h ? [...h, 1] : null;
}

export const applyH = (h, x, y) => {
  const w = h[6] * x + h[7] * y + h[8];
  return [(h[0] * x + h[1] * y + h[2]) / w, (h[3] * x + h[4] * y + h[5]) / w];
};

const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

// corners: [topLeft, topRight, bottomRight, bottomLeft] in source pixels
export function outputSize(corners, max = 2000) {
  const [tl, tr, br, bl] = corners;
  let w = Math.max(dist(tl, tr), dist(bl, br));
  let h = Math.max(dist(tl, bl), dist(tr, br));
  const s = Math.min(3, max / Math.max(w, h));
  return [Math.max(1, Math.round(w * s)), Math.max(1, Math.round(h * s))];
}

export function warp(srcCanvas, corners, max = 2000) {
  const [W, H] = outputSize(corners, max);
  const h = homography([[0, 0], [W, 0], [W, H], [0, H]], corners);
  const sw = srcCanvas.width;
  const sh = srcCanvas.height;
  const src = srcCanvas.getContext("2d").getImageData(0, 0, sw, sh).data;
  const out = document.createElement("canvas");
  out.width = W;
  out.height = H;
  const g = out.getContext("2d");
  const img = g.createImageData(W, H);
  const d = img.data;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const [sx, sy] = applyH(h, x + 0.5, y + 0.5);
      const fx = Math.min(sw - 1.001, Math.max(0, sx - 0.5));
      const fy = Math.min(sh - 1.001, Math.max(0, sy - 0.5));
      const x0 = fx | 0, y0 = fy | 0, ax = fx - x0, ay = fy - y0;
      const i00 = (y0 * sw + x0) * 4, i10 = i00 + 4, i01 = i00 + sw * 4, i11 = i01 + 4;
      const di = (y * W + x) * 4;
      for (let k = 0; k < 3; k++) {
        const top = src[i00 + k] * (1 - ax) + src[i10 + k] * ax;
        const bot = src[i01 + k] * (1 - ax) + src[i11 + k] * ax;
        d[di + k] = top * (1 - ay) + bot * ay;
      }
      d[di + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  return out;
}

export function rotate90(canvas) {
  const c = document.createElement("canvas");
  c.width = canvas.height;
  c.height = canvas.width;
  const g = c.getContext("2d");
  g.translate(c.width, 0);
  g.rotate(Math.PI / 2);
  g.drawImage(canvas, 0, 0);
  return c;
}

// Otsu threshold for a 0-255 gray array
function otsu(gray) {
  const hist = new Array(256).fill(0);
  for (const v of gray) hist[v]++;
  const total = gray.length;
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0, wB = 0, best = 0, thr = 128;
  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (!wB) continue;
    const wF = total - wB;
    if (!wF) break;
    sumB += t * hist[t];
    const mB = sumB / wB, mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) ** 2;
    if (between > best) { best = between; thr = t; }
  }
  return thr;
}

const quadArea = (q) => Math.abs(q.reduce((s, [x, y], i) => { const [x2, y2] = q[(i + 1) % 4]; return s + x * y2 - x2 * y; }, 0)) / 2;

// Finds the bright receipt on a darker background; returns 4 normalized corners [TL, TR, BR, BL] or null
export function detectCornersGray(gray, w, h) {
  const thr = otsu(gray);
  let fg = 0, bg = 0, nf = 0, nb = 0;
  for (const v of gray) v > thr ? ((fg += v), nf++) : ((bg += v), nb++);
  if (!nf || !nb || fg / nf - bg / nb < 35) return null;
  const seen = new Uint8Array(w * h);
  let bestPix = null;
  for (let i = 0; i < w * h; i++) {
    if (seen[i] || gray[i] <= thr) continue;
    const stack = [i], pix = [];
    seen[i] = 1;
    while (stack.length) {
      const p = stack.pop();
      pix.push(p);
      const x = p % w, y = (p / w) | 0;
      for (const q of [x > 0 && p - 1, x < w - 1 && p + 1, y > 0 && p - w, y < h - 1 && p + w]) {
        if (q !== false && !seen[q] && gray[q] > thr) { seen[q] = 1; stack.push(q); }
      }
    }
    if (!bestPix || pix.length > bestPix.length) bestPix = pix;
  }
  if (!bestPix) return null;
  const frac = bestPix.length / (w * h);
  if (frac < 0.12 || frac > 0.97) return null;
  let tl, tr, br, bl;
  let sMin = Infinity, sMax = -Infinity, dMin = Infinity, dMax = -Infinity;
  for (const p of bestPix) {
    const x = p % w, y = (p / w) | 0;
    if (x + y < sMin) { sMin = x + y; tl = [x, y]; }
    if (x + y > sMax) { sMax = x + y; br = [x, y]; }
    if (x - y > dMax) { dMax = x - y; tr = [x, y]; }
    if (x - y < dMin) { dMin = x - y; bl = [x, y]; }
  }
  const quad = [tl, tr, br, bl];
  if (quadArea(quad) < bestPix.length * 0.75) return null;
  return quad.map(([x, y]) => [(x + 0.5) / w, (y + 0.5) / h]);
}

export function detectCorners(canvas) {
  const s = Math.min(1, 320 / Math.max(canvas.width, canvas.height));
  const w = Math.max(8, Math.round(canvas.width * s)), h = Math.max(8, Math.round(canvas.height * s));
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const g = c.getContext("2d");
  g.filter = "blur(1px)";
  g.drawImage(canvas, 0, 0, w, h);
  const d = g.getImageData(0, 0, w, h).data;
  const gray = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) gray[i] = (d[i * 4] * 299 + d[i * 4 + 1] * 587 + d[i * 4 + 2] * 114) / 1000;
  return detectCornersGray(gray, w, h);
}
